import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/page-header'
import { CalendarClock, ClipboardList, Loader2, PlusCircle, UserRound } from 'lucide-react'
import { StatusBadge } from '@/components/status-badge'
import { StatCard } from '@/components/ui/stat'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api/client'
import { ErrorState } from '@/src/components/ErrorState'
import { LoadingState } from '@/src/components/LoadingState'
import { useState } from 'react'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { inquiryStatusCount, loadOverview } from '@/src/lib/overview'
import { inquirySubtitle, inquiryTitle } from './inquirySummary'
import { Pagination } from '@/src/components/Pagination'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { InquiryQuickEdit, QuickEditFailure } from '@/src/components/QuickEdit'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { useOptimisticRows } from '@/src/hooks/useOptimisticRows'
import { useRowSelection, useSelectionKeys } from '@/src/hooks/useRowSelection'
import {
  InquiryBatchActions,
  SelectAllCheckbox,
  SelectCheckbox,
  SelectModeButton,
  selectColumnClass,
} from '@/src/components/BatchActions'
import { RecordCard, RecordCards, RecordFact } from '@/src/components/RecordCard'
import { cn } from '@/lib/utils'
import type { Inquiry } from '@/lib/api/types'

const NO_INQUIRIES: Inquiry[] = []

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleDateString()
}

function customerName(inquiry: Inquiry) {
  return inquiry.customer?.company_name || inquiry.customer?.name || 'Customer not linked'
}

type InquiryView = 'all' | 'mine' | 'triage' | 'cancelled'

// Linear's split: everything, what I own, and what nobody owns yet. Cancelled
// inquiries stay out of the way, in a view of their own.
const VIEWS: Array<{ value: InquiryView; label: string; empty: string }> = [
  { value: 'all', label: 'All', empty: 'No inquiries have been created yet.' },
  { value: 'mine', label: 'My work', empty: 'Nothing open is assigned to you.' },
  { value: 'triage', label: 'Unassigned', empty: 'Every open inquiry has an owner.' },
  { value: 'cancelled', label: 'Cancelled', empty: 'No inquiry has been cancelled.' },
]

const VIEW_FILTERS: Record<
  InquiryView,
  { owner?: 'me' | 'unassigned'; open?: boolean; cancelled?: 'exclude' | 'only' }
> = {
  all: { cancelled: 'exclude' },
  mine: { owner: 'me', open: true },
  triage: { owner: 'unassigned', open: true },
  cancelled: { cancelled: 'only' },
}

function isView(value: string | null): value is InquiryView {
  return VIEWS.some((entry) => entry.value === value)
}

export function InquiriesPage() {
  const [page, setPage] = useState(1)
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('view')
  const view: InquiryView = isView(requested) ? requested : 'all'
  const [taking, setTaking] = useState<string | null>(null)
  const {
    data: result,
    loading,
    error,
    reload,
  } = useAsyncData(() => apiClient.listInquiriesPage(page, 25, VIEW_FILTERS[view]), [page, view])
  // Quick edits show on the row before the API answers.
  const edits = useOptimisticRows(result?.items ?? NO_INQUIRIES)
  const rows = edits.rows
  // Counts span every page; `rows` is only this one.
  const overview = useAsyncData(loadOverview, [])
  const people = useHelmPeople()
  const canEdit = Boolean(people.data?.canEdit)
  // Picked rows, for changing many at once; kept across pages and views.
  const selection = useRowSelection<Inquiry>()
  useSelectionKeys(selection, rows, canEdit)

  function changeView(next: string) {
    setPage(1)
    setSearchParams(next === 'all' ? {} : { view: next }, { replace: true })
  }

  async function takeIt(inquiry: Inquiry) {
    const meId = people.data?.meId
    if (!meId) return
    setTaking(inquiry.id)
    try {
      await apiClient.updateInquiry(inquiry.id, { owner_user_id: meId })
      reload()
    } finally {
      setTaking(null)
    }
  }

  function ownerName(inquiry: Inquiry) {
    if (!inquiry.owner_user_id) return 'Unassigned'
    if (inquiry.owner_user_id === people.data?.meId) return 'You'
    return personName(people.data?.users.get(inquiry.owner_user_id))
  }

  if (loading && !result) return <LoadingState label="Loading inquiries..." />
  if (error && !result) return <ErrorState error={error} onRetry={reload} />
  const emptyMessage = VIEWS.find((entry) => entry.value === view)!.empty

  return (
    // Room under the last row for the batch bar or, on a phone, Beacon's button.
    <div className={cn('space-y-6', selection.count > 0 ? 'pb-40 md:pb-20' : 'pb-20 md:pb-0')}>
      <PageHeader
        title="Inquiries"
        description="Qualify sourcing, shipping, PVoC, testing, and clearance requests before operations begin."
        actions={
          <>
            {canEdit && <SelectModeButton selection={selection} />}
            <Button asChild variant="brand">
              <Link to="/inquiries/new">
                <PlusCircle className="h-4 w-4" />
                New Inquiry
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {(['SUBMITTED', 'QUALIFYING', 'QUOTED', 'ACTIVE'] as const).map((status) => (
          <StatCard
            key={status}
            label={formatLabel(status)}
            icon={ClipboardList}
            value={overview.loading ? '—' : inquiryStatusCount(overview.data, rows, status)}
          />
        ))}
      </div>

      <Tabs value={view} onValueChange={changeView}>
        <TabsList aria-label="Which inquiries">
          {VIEWS.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <QuickEditFailure edits={edits} />

      <Card data-tour="inquiry-list">
        {rows.length === 0 ? (
          <p className="px-6 text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <Table className="max-md:hidden">
            <TableHeader>
              <TableRow>
                {canEdit && (
                  <TableHead className={selectColumnClass(selection)}>
                    <SelectAllCheckbox selection={selection} visible={rows} noun="inquiry" />
                  </TableHead>
                )}
                <TableHead>Request</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Next Action</TableHead>
                {canEdit && (
                  // Pinned to the right, so a phone reaches it without scrolling the table.
                  <TableHead className="sticky right-0 w-12 bg-card">
                    <span className="sr-only">Quick edit</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inquiry) => (
                <TableRow
                  key={inquiry.id}
                  className="group"
                  {...(canEdit ? selection.rowProps(inquiry, rows) : {})}
                >
                  {canEdit && (
                    <TableCell className={selectColumnClass(selection)}>
                      <SelectCheckbox
                        checked={selection.isSelected(inquiry.id)}
                        label={`Select ${inquiryTitle(inquiry)}`}
                        onToggle={(range) => selection.toggle(inquiry, rows, range)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Link to={`/inquiries/${inquiry.id}`} className="font-medium hover:underline">
                      {inquiryTitle(inquiry)}
                    </Link>
                    <div className="text-xs text-muted-foreground">{inquirySubtitle(inquiry)}</div>
                  </TableCell>
                  <TableCell>{customerName(inquiry)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {inquiry.owner_user_id ? (
                      inquiry.owner_user_id === people.data?.meId ? (
                        'You'
                      ) : (
                        personName(people.data?.users.get(inquiry.owner_user_id))
                      )
                    ) : people.data?.meId ? (
                      <Button
                        variant="outline"
                        size="xs"
                        disabled={taking === inquiry.id}
                        onClick={() => void takeIt(inquiry)}
                      >
                        {taking === inquiry.id && <Loader2 className="animate-spin" />}
                        Take it
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={inquiry.status} />
                  </TableCell>
                  <TableCell>{formatLabel(inquiry.priority)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDate(inquiry.sla_due_at)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[280px] truncate">
                    {inquiry.next_action || 'Not set'}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="sticky right-0 bg-card py-1 group-data-[state=selected]:bg-muted">
                      <InquiryQuickEdit inquiry={inquiry} edits={edits} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {rows.length > 0 && (
          <RecordCards
            label="Inquiries"
            noun="inquiry"
            rows={rows}
            selection={canEdit ? selection : undefined}
          >
            {rows.map((inquiry) => (
              <RecordCard
                key={inquiry.id}
                row={inquiry}
                rows={rows}
                href={`/inquiries/${inquiry.id}`}
                code={inquiry.reference_code}
                title={inquiryTitle(inquiry)}
                status={inquiry.status}
                selection={canEdit ? selection : undefined}
                selectLabel={`Select ${inquiryTitle(inquiry)}`}
                details={
                  <>
                    <RecordFact className="font-medium text-foreground">
                      {customerName(inquiry)}
                    </RecordFact>
                    <RecordFact icon={UserRound} label="Owner">
                      {ownerName(inquiry)}
                    </RecordFact>
                    {inquiry.sla_due_at && (
                      <RecordFact icon={CalendarClock} label="Due">
                        {formatDate(inquiry.sla_due_at)}
                      </RecordFact>
                    )}
                  </>
                }
                next={inquiry.next_action}
                action={canEdit && <InquiryQuickEdit inquiry={inquiry} edits={edits} />}
              />
            ))}
          </RecordCards>
        )}

        {result && rows.length > 0 && (
          <Pagination
            className="px-4 py-3 md:px-6"
            page={result.page}
            limit={result.limit}
            total={result.total}
            totalPages={result.totalPages}
            onPageChange={setPage}
            noun="inquiries"
          />
        )}
      </Card>

      {canEdit && (
        <InquiryBatchActions
          selection={selection}
          edits={edits}
          people={people.data?.users ?? new Map()}
          meId={people.data?.meId ?? null}
        />
      )}
    </div>
  )
}
